/// <reference types="vite/client" />
import { config } from "@repo/config";
import { ConsentBanner } from "@shared/components/ConsentBanner";
import { Logo } from "@shared/components/Logo";
import { NavigationProgress } from "@shared/components/NavigationProgress";
import { ThemeProvider } from "@shared/components/ThemeProvider";
import { siteShareCard } from "@shared/lib/meta";
import { createQueryClient } from "@shared/lib/query-client";
import { themeScript } from "@shared/stores/theme-store";
import { QueryClientProvider } from "@tanstack/react-query";
import {
	createRootRoute,
	type ErrorComponentProps,
	HeadContent,
	Outlet,
	Scripts,
	useMatches,
} from "@tanstack/react-router";
import { Button } from "@ui/components/button";
import { AnchorLinkButton } from "@ui/components/link-button";
import { Toaster } from "@ui/components/toast";
import { TooltipProvider } from "@ui/components/tooltip";
import { CompassIcon, RotateCwIcon, TriangleAlertIcon } from "lucide-react";
import React, { Suspense, useState } from "react";
import { webMcpScript } from "../agents/webmcp-script";

// Lazy load devtools - only bundled in development
const ReactQueryDevtools = import.meta.env.DEV
	? React.lazy(() =>
			import("@tanstack/react-query-devtools").then((m) => ({
				default: m.ReactQueryDevtools,
			})),
		)
	: () => null;

const TanStackRouterDevtools = import.meta.env.DEV
	? React.lazy(() =>
			import("@tanstack/react-router-devtools").then((m) => ({
				default: m.TanStackRouterDevtools,
			})),
		)
	: () => null;

// Import global styles as URL for proper SSR (prevents FOUC)
import appCss from "../globals.css?url";
import siteCss from "../site.css?url";

const appCssHref = `${appCss}?v=${import.meta.env.VITE_ASSET_VERSION}`;
// The public site's own, much smaller stylesheet (app/site.css).
const siteCssHref = `${siteCss}?v=${import.meta.env.VITE_ASSET_VERSION}`;

// Optional Google Search Console verification token. Only emitted when set so
// the meta tag never ships empty.
const gscVerification = import.meta.env.VITE_GSC_VERIFICATION as
	| string
	| undefined;

// Reload on stale-chunk errors. After a deploy, content-hashed chunk filenames
// change; tabs opened before the deploy 404 when they lazy-import old chunks
// (Vite emits `vite:preloadError`; Safari surfaces it as the "'text/html' is
// not a valid JavaScript MIME type" TypeError when the SPA shell is returned
// in place of the missing JS). One hard reload pulls the fresh index.html with
// the new hashes. The 10s sessionStorage guard prevents a reload loop if the
// failure is something else.
const chunkReloadScript =
	"(function(){function reload(){try{var k='__chunk_reload_at__';" +
	"var last=parseInt(sessionStorage.getItem(k)||'0',10);" +
	"if(Date.now()-last<10000)return;" +
	"sessionStorage.setItem(k,String(Date.now()));}catch(e){}" +
	"window.location.reload();}" +
	"window.addEventListener('vite:preloadError',reload);" +
	"window.addEventListener('error',function(e){" +
	"var msg=(e&&(e.message||(e.error&&e.error.message)))||'';" +
	"if(msg.indexOf('not a valid JavaScript MIME type')!==-1||" +
	"msg.indexOf('Failed to fetch dynamically imported module')!==-1||" +
	"msg.indexOf('Importing a module script failed')!==-1){reload();}});})();";

// SVG for browsers, ICO for old ones, and a 96px PNG for Google Search,
// which stopped accepting SVG favicons in August 2026. Generated by
// apps/web/brand/render.sh.
declare module "@tanstack/react-router" {
	interface StaticDataRouteOption {
		/**
		 * The retailer's page, not ours (the hosted chat): it links its own icons, and the
		 * root adds no analytics and no consent banner.
		 */
		retailerPage?: boolean;
	}
}

const siteIcons = [
	{ rel: "icon", href: "/favicon.ico", sizes: "32x32" },
	{ rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
	{
		rel: "icon",
		type: "image/png",
		sizes: "96x96",
		href: "/favicon-96x96.png",
	},
	{ rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
	{ rel: "manifest", href: "/site.webmanifest" },
];

export const Route = createRootRoute({
	head: ({ matches }) => {
		const retailerPage = matches.some((m) => m.staticData?.retailerPage);
		// The public site's pages expose WebMCP tools (app/agents/webmcp-script.ts).
		const sitePage = matches.some((m) =>
			m.routeId.startsWith("/_marketing"),
		);
		const cssHref = sitePage ? siteCssHref : appCssHref;
		const publicPage = matches.some(
			(m) =>
				m.routeId.startsWith("/_marketing") ||
				m.routeId.startsWith("/legal/"),
		);
		return {
			meta: [
				{ charSet: "utf-8" },
				{
					name: "viewport",
					content: "width=device-width, initial-scale=1",
				},
				{ title: config.appName },
				// Only the home page is indexed; it overrides this.
				{ name: "robots", content: "noindex" },
				{ name: "application-name", content: config.appName },
				{ name: "apple-mobile-web-app-title", content: config.appName },
				// The light theme's background (--background).
				{ name: "theme-color", content: "#fdfcfb" },
				// Stop iOS Safari from auto-linking digit strings as phone numbers.
				{ name: "format-detection", content: "telephone=no" },
				// Link previews (WhatsApp, Facebook, LinkedIn, Slack, iMessage).
				// X reads these too; it only needs twitter:card.
				{ property: "og:type", content: "website" },
				{ property: "og:site_name", content: config.appName },
				{ property: "og:locale", content: "en_US" },
				{ property: "og:image", content: siteShareCard.url },
				{ property: "og:image:type", content: "image/png" },
				{
					property: "og:image:width",
					content: String(siteShareCard.width),
				},
				{
					property: "og:image:height",
					content: String(siteShareCard.height),
				},
				{ property: "og:image:alt", content: siteShareCard.alt },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image:alt", content: siteShareCard.alt },
				...(gscVerification
					? [
							{
								name: "google-site-verification",
								content: gscVerification,
							},
						]
					: []),
			],
			scripts: [
				// Registered first so its error listeners are in place before any
				// later script could throw a stale-chunk error.
				{ children: chunkReloadScript },
				...(publicPage ? [{ children: webMcpScript }] : []),
			],
			links: [
				// Preload CSS for faster initial render (prevents FOUC)
				{ rel: "preload", href: cssHref, as: "style" },
				{ rel: "stylesheet", href: cssHref },
				...(retailerPage ? [] : siteIcons),
			],
		};
	},
	component: RootComponent,
	errorComponent: GlobalErrorComponent,
	notFoundComponent: NotFoundComponent,
});

function RootComponent() {
	// Create QueryClient once per component instance
	// Server: new instance per request (component remounts)
	// Client: stable instance across re-renders (useState initializer)
	const [queryClient] = useState(() => createQueryClient());

	return (
		<RootDocument>
			<QueryClientProvider client={queryClient}>
				<ThemeProvider>
					<NavigationProgress />
					{/* viewTransitionName pairs with router.tsx's
					    defaultViewTransition for direction-aware page slides.
					    See theme.css § VIEW TRANSITIONS. */}
					<TooltipProvider>
						<div
							className="isolate"
							style={{ viewTransitionName: "main-content" }}
						>
							<Outlet />
						</div>
					</TooltipProvider>
					<Toaster />
				</ThemeProvider>
				<Suspense fallback={null}>
					<ReactQueryDevtools buttonPosition="bottom-left" />
				</Suspense>
			</QueryClientProvider>
		</RootDocument>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	// The hosted chat is a retailer's page inside a retailer's app: no analytics of ours and
	// no consent banner. It also keeps a chat loaded ahead of the tap out of page views.
	const shopperPage = useMatches({
		select: (matches) => matches.some((m) => m.staticData?.retailerPage),
	});
	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				<HeadContent />
				{/* Theme script to prevent flash of wrong theme */}
				{/* biome-ignore lint/security/noDangerouslySetInnerHtml: Required for theme initialization before hydration */}
				<script dangerouslySetInnerHTML={{ __html: themeScript }} />
			</head>
			<body className="min-h-dvh bg-background text-foreground antialiased">
				{children}
				{shopperPage ? null : (
					<>
						{/* No-ops unless VITE_GOOGLE_ANALYTICS_ID is configured. */}
						<ConsentBanner />
					</>
				)}
				<Suspense fallback={null}>
					<TanStackRouterDevtools position="bottom-right" />
				</Suspense>
				<Scripts />
			</body>
		</html>
	);
}

function GlobalErrorComponent({ error }: ErrorComponentProps) {
	return (
		<RootDocument>
			<ErrorScreen
				icon={<TriangleAlertIcon />}
				title="Something went wrong"
				description="The page hit an error it couldn't recover from. Reloading usually fixes it. If it keeps happening, tell us what you were doing."
				detail={
					import.meta.env.DEV
						? error instanceof Error
							? error.message
							: String(error)
						: undefined
				}
				actions={
					<>
						<Button onClick={() => window.location.reload()}>
							<RotateCwIcon data-icon="inline-start" />
							Reload page
						</Button>
						<AnchorLinkButton href="/app" variant="outline">
							Open the admin
						</AnchorLinkButton>
					</>
				}
			/>
		</RootDocument>
	);
}

function NotFoundComponent() {
	return (
		<ErrorScreen
			icon={<CompassIcon />}
			title="This page doesn't exist"
			description="The link may be old or mistyped."
			actions={
				<>
					<AnchorLinkButton href="/app">
						Open the admin
					</AnchorLinkButton>
					<Button
						variant="outline"
						onClick={() => window.history.back()}
					>
						Go back
					</Button>
				</>
			}
		/>
	);
}

/** Full-screen state for errors outside the app shell. */
function ErrorScreen({
	icon,
	title,
	description,
	detail,
	actions,
}: {
	icon: React.ReactNode;
	title: string;
	description: string;
	detail?: string;
	actions: React.ReactNode;
}) {
	return (
		<div className="flex min-h-svh flex-col bg-background px-5 py-5 sm:px-10 sm:py-8">
			<a href="/app" className="w-fit">
				<Logo />
			</a>
			<main className="flex flex-1 items-center justify-center py-12">
				<div className="flex w-full max-w-md flex-col gap-6">
					<span className="grid size-11 place-items-center rounded-2xl bg-muted text-muted-foreground [&_svg]:size-5">
						{icon}
					</span>
					<div className="flex flex-col gap-2">
						<h1 className="text-balance font-semibold text-2xl tracking-tight">
							{title}
						</h1>
						<p className="text-pretty text-muted-foreground text-sm">
							{description}
						</p>
					</div>
					{detail ? (
						<pre className="max-h-48 overflow-auto rounded-2xl bg-muted p-4 font-mono text-muted-foreground text-xs">
							{detail}
						</pre>
					) : null}
					<div className="flex flex-wrap gap-2">{actions}</div>
				</div>
			</main>
		</div>
	);
}
